import { useState, useEffect } from 'react';
import { getAll, add, update, remove, STORES } from '../db';
import { supabase } from '../lib/supabase';
import { loadOverheadSettings } from './FixedCosts';
import { Search, Plus, ChevronDown, ChevronUp, Trash2, Edit3, Save, X, Clock, Info } from 'lucide-react';

const SKIN_CONDITIONS = ['Acne', 'Rosacea', 'Pigmentation', 'Ageing', 'Sensitive', 'Dehydration', 'Scarring', 'Melasma', 'Thread Veins', 'Hair Loss', 'Sun Damage', 'Dull Skin', 'Large Pores', 'Wrinkles', 'Volume Loss', 'Hyperhidrosis'];
const TX_CATEGORIES = ['Anti-Wrinkle Injections', 'Dermal Fillers', 'Skin Boosters', 'Mesotherapy', 'Microneedling', 'Chemical Peels', 'Medi-Facials', 'LED Light Therapy', 'HIFU', 'Laser & IPL', 'Cryotherapy', 'Microsclerotherapy', 'PRP', 'Blood Tests', 'Minor Surgery', 'Consultations'];
const OVERHEAD_CATS = [
  { key: 'cat_clinic_consumables', label: 'Clinic Consumables' },
  { key: 'cat_front_of_house',     label: 'Front of House' },
  { key: 'cat_janitorial',         label: 'Janitorial' },
  { key: 'cat_back_bar',           label: 'Back Bar' },
  { key: 'cat_emergency',          label: 'Emergency Supplies' },
];

const emptyTreatment = {
  name: '', category: '', rrp: '', duration: '', description: '',
  skinConditions: [], contraindications: '', aftercare: '', preTxAdvice: '',
  stockItems: [], consumables: [],
  notes: '', status: 'Active',
};

export default function Treatments() {
  const [treatments, setTreatments] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [overhead, setOverhead] = useState(null);
  const [search, setSearch] = useState('');
  const [conditionFilter, setConditionFilter] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyTreatment);
  const [showNew, setShowNew] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  useEffect(() => { load(); }, []);

  const load = async () => {
    const [txs, stock, oh] = await Promise.all([
      getAll(STORES.treatments),
      supabase.from('stock').select('id, name, category, unit, costPerUnit, vatRate').eq('category', 'Treatment Stock').order('name'),
      loadOverheadSettings(),
    ]);
    setTreatments(txs.sort((a, b) => a.name?.localeCompare(b.name)));
    setStockItems(stock.data || []);
    setOverhead(oh);
  };

  const filtered = treatments.filter(t => {
    const matchSearch = `${t.name} ${t.category}`.toLowerCase().includes(search.toLowerCase());
    const matchCond = !conditionFilter || (t.skinConditions || []).includes(conditionFilter);
    return matchSearch && matchCond;
  });

  const calcStockCost = (tx) => {
    return (tx.stockItems || []).reduce((s, item) => {
      return s + (parseFloat(item.netCostPerUnit) || 0) * (parseFloat(item.qty) || 1);
    }, 0);
  };

  const calcOverheadCost = (tx) => {
    if (!overhead || !tx.duration) return 0;
    const hrs = parseFloat(tx.duration) / 60;
    return overhead.costPerHour * hrs;
  };

  const calcTotalNetCost = (tx) => {
    const stock = calcStockCost(tx);
    const oh = calcOverheadCost(tx);
    const consumables = (tx.consumables || []).reduce((s, c) => s + (parseFloat(c.cost) || 0), 0);
    return stock + oh + consumables;
  };

  const calcGrossCost = (tx) => {
    // Gross = net stock costs inc VAT (average 20%) + overhead (no VAT uplift on overhead)
    const stockNet = calcStockCost(tx);
    const oh = calcOverheadCost(tx);
    const consumables = (tx.consumables || []).reduce((s, c) => s + (parseFloat(c.cost) || 0), 0);
    return stockNet * 1.2 + oh + consumables;
  };

  const margin = (tx) => {
    const cost = calcTotalNetCost(tx);
    if (!tx.rrp || !cost) return null;
    return Math.round(((parseFloat(tx.rrp) - cost) / parseFloat(tx.rrp)) * 100);
  };

  const maxDiscount = (tx) => {
    const cost = calcTotalNetCost(tx);
    if (!tx.rrp || !cost) return null;
    return Math.round((parseFloat(tx.rrp) - cost) * 0.7);
  };

  const toggleCondition = (cond, obj, setObj) => {
    const current = obj.skinConditions || [];
    setObj({ ...obj, skinConditions: current.includes(cond) ? current.filter(c => c !== cond) : [...current, cond] });
  };

  const saveNew = async () => {
    if (!form.name) return;
    await add(STORES.treatments, form);
    setForm(emptyTreatment);
    setShowNew(false);
    load();
  };

  const saveEdit = async () => {
    await update(STORES.treatments, editing);
    setEditing(null);
    load();
  };

  const deleteTreatment = async (id) => {
    if (!window.confirm('Delete this treatment?')) return;
    await remove(STORES.treatments, id);
    setExpanded(null);
    load();
  };

  const TreatmentForm = ({ data, setData, onSave, onCancel }) => (
    <div className="patient-form">
      <div className="form-section-title">Treatment Details</div>
      <div className="form-grid">
        <div className="form-field full"><label>Treatment Name *</label><input value={data.name} onChange={e => setData({ ...data, name: e.target.value })} /></div>
        <div className="form-field"><label>Category</label>
          <select value={data.category} onChange={e => setData({ ...data, category: e.target.value })}>
            <option value="">Select...</option>
            {TX_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="form-field"><label>RRP £</label><input type="number" step="0.01" value={data.rrp} onChange={e => setData({ ...data, rrp: e.target.value })} /></div>
        <div className="form-field"><label>Duration (mins)</label><input type="number" value={data.duration} onChange={e => setData({ ...data, duration: e.target.value })} /></div>
        <div className="form-field"><label>Status</label>
          <select value={data.status} onChange={e => setData({ ...data, status: e.target.value })}>
            <option value="Active">Active</option>
            <option value="Coming Soon">Coming Soon</option>
            <option value="Discontinued">Discontinued</option>
          </select>
        </div>
      </div>

      <div className="form-section-title" style={{ marginTop: '1rem' }}>Go-To For (Skin Conditions)</div>
      <div className="condition-pills">
        {SKIN_CONDITIONS.map(c => (
          <button key={c} className={`condition-pill ${(data.skinConditions || []).includes(c) ? 'active' : ''}`}
            onClick={() => toggleCondition(c, data, setData)}>{c}</button>
        ))}
      </div>

      <div className="form-section-title" style={{ marginTop: '1rem' }}>Treatment Stock Items</div>
      <p style={{ fontSize: '12px', color: 'var(--soft)', marginBottom: '8px' }}>
        Items from your Treatment Stock (specific quantities used per treatment). Add stock items on the Stock page first.
      </p>
      {(data.stockItems || []).map((item, i) => {
        const stockItem = stockItems.find(s => s.id === item.stockId);
        return (
          <div key={i} className="linked-item">
            <select value={item.stockId} onChange={e => {
              const s = stockItems.find(x => x.id === e.target.value);
              const updated = [...(data.stockItems || [])];
              updated[i] = { ...updated[i], stockId: e.target.value, name: s?.name || '', unit: s?.unit || '', netCostPerUnit: s?.costPerUnit || '' };
              setData({ ...data, stockItems: updated });
            }}>
              <option value="">Select stock item...</option>
              {stockItems.map(s => <option key={s.id} value={s.id}>{s.name} {s.unit ? `(per ${s.unit})` : ''}</option>)}
            </select>
            <input type="number" step="0.001" placeholder="Qty" value={item.qty} style={{ width: '70px' }}
              onChange={e => { const u = [...(data.stockItems || [])]; u[i] = { ...u[i], qty: e.target.value }; setData({ ...data, stockItems: u }); }} />
            <span style={{ fontSize: '12px', color: 'var(--soft)', whiteSpace: 'nowrap' }}>
              {item.netCostPerUnit && item.qty ? `£${(parseFloat(item.netCostPerUnit) * parseFloat(item.qty)).toFixed(4)}` : ''}
            </span>
            <button className="icon-btn danger" onClick={() => setData({ ...data, stockItems: (data.stockItems || []).filter((_, j) => j !== i) })}><X size={12} /></button>
          </div>
        );
      })}
      <button className="btn-ghost small" onClick={() => setData({ ...data, stockItems: [...(data.stockItems || []), { stockId: '', name: '', qty: '', unit: '', netCostPerUnit: '' }] })}>
        <Plus size={12} /> Add stock item
      </button>

      <div className="form-section-title" style={{ marginTop: '1rem' }}>Other Consumables</div>
      {(data.consumables || []).map((c, i) => (
        <div key={i} className="linked-item">
          <input placeholder="Item name" value={c.name} onChange={e => { const cons = [...data.consumables]; cons[i].name = e.target.value; setData({ ...data, consumables: cons }); }} />
          <input type="number" step="0.01" placeholder="Cost £" value={c.cost} style={{ width: '90px' }}
            onChange={e => { const cons = [...data.consumables]; cons[i].cost = e.target.value; setData({ ...data, consumables: cons }); }} />
          <button className="icon-btn danger" onClick={() => setData({ ...data, consumables: data.consumables.filter((_, j) => j !== i) })}><X size={12} /></button>
        </div>
      ))}
      <button className="btn-ghost small" onClick={() => setData({ ...data, consumables: [...(data.consumables || []), { name: '', cost: '' }] })}>
        <Plus size={12} /> Add other consumable
      </button>

      <div className="form-section-title" style={{ marginTop: '1rem' }}>Description & Advice</div>
      <div className="form-grid">
        <div className="form-field full"><label>Description</label><textarea value={data.description} onChange={e => setData({ ...data, description: e.target.value })} rows={3} /></div>
        <div className="form-field full"><label>Pre-Treatment Advice</label><textarea value={data.preTxAdvice} onChange={e => setData({ ...data, preTxAdvice: e.target.value })} rows={3} /></div>
        <div className="form-field full"><label>Aftercare</label><textarea value={data.aftercare} onChange={e => setData({ ...data, aftercare: e.target.value })} rows={3} /></div>
        <div className="form-field full"><label>Contraindications</label><textarea value={data.contraindications} onChange={e => setData({ ...data, contraindications: e.target.value })} rows={2} /></div>
        <div className="form-field full"><label>Notes</label><textarea value={data.notes} onChange={e => setData({ ...data, notes: e.target.value })} rows={2} /></div>
      </div>
      <div className="form-actions">
        <button className="btn-ghost" onClick={onCancel}><X size={14} /> Cancel</button>
        <button className="btn-gold" onClick={onSave}><Save size={14} /> Save Treatment</button>
      </div>
    </div>
  );

  return (
    <div className="section-content">
      <div className="section-header-row">
        <div>
          <h2 className="section-title">Treatments</h2>
          <p className="section-sub">{treatments.length} treatment{treatments.length !== 1 ? 's' : ''}</p>
        </div>
        <button className="btn-gold" onClick={() => setShowNew(true)}><Plus size={14} /> Add Treatment</button>
      </div>

      <div className="search-bar">
        <Search size={15} className="search-icon" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search treatments..." className="search-input" />
        <select value={conditionFilter} onChange={e => setConditionFilter(e.target.value)} className="filter-select">
          <option value="">All conditions</option>
          {SKIN_CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {conditionFilter && (
        <div className="condition-filter-notice">
          Showing treatments for: <strong>{conditionFilter}</strong>
          <button onClick={() => setConditionFilter('')}><X size={12} /></button>
        </div>
      )}

      {showNew && (
        <div className="patient-card expanded" style={{ marginBottom: '1rem' }}>
          <div className="card-header"><div className="patient-name-block"><div className="patient-initials">+</div><div className="patient-name">New Treatment</div></div></div>
          <TreatmentForm data={form} setData={setForm} onSave={saveNew} onCancel={() => { setShowNew(false); setForm(emptyTreatment); }} />
        </div>
      )}

      <div className="patient-list">
        {filtered.map(t => {
          const cost = calcTotalNetCost(t);
          const gross = calcGrossCost(t);
          const m = margin(t);
          const maxDisc = maxDiscount(t);
          return (
            <div key={t.id} className={`patient-card ${expanded === t.id ? 'expanded' : ''}`}>
              <div className="card-header" onClick={() => { setExpanded(expanded === t.id ? null : t.id); setEditing(null); setActiveTab('details'); }}>
                <div className="patient-name-block">
                  <div className="patient-initials" style={{ fontSize: '10px' }}>{t.category?.[0] || t.name?.[0]}</div>
                  <div>
                    <div className="patient-name">
                      {t.name}
                      {t.status === 'Coming Soon' && <span className="status-badge future">Coming Soon</span>}
                    </div>
                    <div className="patient-meta">
                      {t.category && <span>{t.category}</span>}
                      {t.duration && <span><Clock size={11} /> {t.duration} mins</span>}
                      {t.rrp && <span>£{parseFloat(t.rrp).toFixed(2)}</span>}
                      {m !== null && <span className={`margin-badge ${m > 60 ? 'good' : m > 40 ? 'ok' : 'low'}`}>{m}% margin</span>}
                      {(t.skinConditions || []).slice(0, 2).map(c => <span key={c} className="mini-tag">{c}</span>)}
                    </div>
                  </div>
                </div>
                <div className="card-actions" onClick={e => e.stopPropagation()}>
                  <button className="icon-btn" onClick={() => { setEditing(t); setExpanded(t.id); }}><Edit3 size={14} /></button>
                  <button className="icon-btn danger" onClick={() => deleteTreatment(t.id)}><Trash2 size={14} /></button>
                  <span className="expand-icon">{expanded === t.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</span>
                </div>
              </div>

              {expanded === t.id && (
                <div className="card-body">
                  {editing?.id === t.id ? (
                    <TreatmentForm data={editing} setData={setEditing} onSave={saveEdit} onCancel={() => setEditing(null)} />
                  ) : (
                    <>
                      <div className="patient-tabs">
                        {['details', 'costs', 'advice', 'conditions'].map(tab => (
                          <button key={tab} className={`patient-tab ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
                            {tab.charAt(0).toUpperCase() + tab.slice(1)}
                          </button>
                        ))}
                      </div>

                      {activeTab === 'details' && (
                        <div className="detail-grid">
                          <div className="detail-row"><span className="detail-label">Category</span><span>{t.category || '—'}</span></div>
                          <div className="detail-row"><span className="detail-label">Duration</span><span>{t.duration ? `${t.duration} mins` : '—'}</span></div>
                          <div className="detail-row"><span className="detail-label">RRP</span><span>{t.rrp ? `£${parseFloat(t.rrp).toFixed(2)}` : '—'}</span></div>
                          <div className="detail-row full"><span className="detail-label">Description</span><span>{t.description || '—'}</span></div>
                        </div>
                      )}

                      {activeTab === 'costs' && (
                        <div>
                          <div className="cost-summary-cards">
                            <div className="cost-card"><div className="cost-card-label">Net Cost</div><div className="cost-card-val">£{cost.toFixed(2)}</div></div>
                            <div className="cost-card"><div className="cost-card-label">Gross Cost</div><div className="cost-card-val">£{gross.toFixed(2)}</div></div>
                            <div className="cost-card"><div className="cost-card-label">RRP</div><div className="cost-card-val">£{parseFloat(t.rrp || 0).toFixed(2)}</div></div>
                            <div className="cost-card"><div className="cost-card-label">Profit (on net)</div><div className="cost-card-val good">£{(parseFloat(t.rrp || 0) - cost).toFixed(2)}</div></div>
                            <div className="cost-card"><div className="cost-card-label">Margin</div><div className={`cost-card-val ${m > 60 ? 'good' : m > 40 ? 'ok' : 'low'}`}>{m !== null ? `${m}%` : '—'}</div></div>
                            <div className="cost-card"><div className="cost-card-label">Max Safe Discount</div><div className="cost-card-val">£{maxDiscount(t) !== null ? maxDiscount(t) : '—'}</div></div>
                          </div>

                          <div className="cost-breakdown">
                            <div className="cost-breakdown-title">Cost Breakdown</div>

                            {(t.stockItems || []).length > 0 && <>
                              <div className="cost-breakdown-group">Treatment Stock</div>
                              {(t.stockItems || []).map((item, i) => (
                                <div key={i} className="cost-breakdown-row">
                                  <span>{item.name} × {item.qty} {item.unit}</span>
                                  <span>£{(parseFloat(item.netCostPerUnit || 0) * parseFloat(item.qty || 0)).toFixed(4)}</span>
                                </div>
                              ))}
                            </>}

                            {(t.consumables || []).length > 0 && <>
                              <div className="cost-breakdown-group">Other Consumables</div>
                              {(t.consumables || []).map((c, i) => (
                                <div key={i} className="cost-breakdown-row">
                                  <span>{c.name}</span>
                                  <span>{c.cost ? `£${parseFloat(c.cost).toFixed(2)}` : '—'}</span>
                                </div>
                              ))}
                            </>}

                            {overhead && t.duration && <>
                              <div className="cost-breakdown-group">Overheads ({t.duration} mins)</div>
                              <div className="cost-breakdown-row">
                                <span>Fixed overheads @ £{overhead.costPerHour.toFixed(2)}/hr</span>
                                <span>£{calcOverheadCost(t).toFixed(2)}</span>
                              </div>
                              {OVERHEAD_CATS.map(({ key, label }) => {
                                const monthly = parseFloat(overhead.settings[key]) || 0;
                                if (!monthly) return null;
                                const perHour = overhead.hoursPerMonth > 0 ? monthly / overhead.hoursPerMonth : 0;
                                const forTx = perHour * (parseFloat(t.duration) / 60);
                                return (
                                  <div key={key} className="cost-breakdown-row soft">
                                    <span>{label}</span>
                                    <span>£{forTx.toFixed(2)}</span>
                                  </div>
                                );
                              })}
                            </>}

                            <div className="cost-breakdown-total">
                              <span>Net Cost Total</span><span>£{cost.toFixed(2)}</span>
                            </div>
                            <div className="cost-breakdown-total gross">
                              <span>Gross Cost Total <span style={{fontWeight:400,fontSize:'11px'}}>(stock inc 20% VAT)</span></span>
                              <span>£{gross.toFixed(2)}</span>
                            </div>
                            {!overhead?.hoursPerMonth && (
                              <p style={{ fontSize: '12px', color: 'var(--amber)', marginTop: '8px' }}>
                                <Info size={12} /> Set working hours on the Fixed Costs page to see overhead calculations.
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {activeTab === 'advice' && (
                        <div className="detail-grid">
                          <div className="detail-row full"><span className="detail-label">Pre-Treatment Advice</span><span>{t.preTxAdvice || '—'}</span></div>
                          <div className="detail-row full"><span className="detail-label">Aftercare</span><span>{t.aftercare || '—'}</span></div>
                          <div className="detail-row full"><span className="detail-label">Contraindications</span><span>{t.contraindications || '—'}</span></div>
                        </div>
                      )}

                      {activeTab === 'conditions' && (
                        <div className="detail-grid">
                          <div className="detail-row full"><span className="detail-label">Go-To For</span>
                            <div className="condition-pills" style={{ marginTop: '4px' }}>
                              {(t.skinConditions || []).length > 0
                                ? t.skinConditions.map(c => <span key={c} className="condition-pill active small">{c}</span>)
                                : <span>No conditions linked</span>}
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {filtered.length === 0 && !showNew && (
          <div className="empty-state"><p>No treatments found</p><button className="btn-gold" onClick={() => setShowNew(true)}><Plus size={14} /> Add your first treatment</button></div>
        )}
      </div>
    </div>
  );
}
